import { Button } from "@progress/kendo-react-buttons";
// @ts-ignore
import Export from "@/components/Export";
import SearchBox from "@/components/SearchBox";
import { useAllLevels, useLevels, useUpdateLevel } from "@/hooks/useLevels";
import { ILevel } from "@/interfaces/level.interface";
import { RootState } from "@/store/store";
import { buildFilters, getPaginationData } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { columnProps } from "@/components/ColumnMenu";
import { queryKeys } from "@/constants/queryKeyFactory";
import { useSearchFilter } from "@/hooks/useSearchFilters";
import { queryClient } from "@/lib/queryClient";
import { levelSvc } from "@/services/level.service";
import { CustomFilter } from "../../interfaces/columns.interface";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Switch, TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Plus } from "lucide-react";
import { createRef, useEffect, useMemo, useState } from "react";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { useAppSelector } from "../../store/hooks/hooks";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { add } from "lodash";

let mappingChanged = false;

export const Listing = () => {
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();
  const updateLevelMutation = useUpdateLevel();

  const [customFilters, setCustomFilters] = useState<CustomFilter[]>([
    {
      field: "parentLevelId",
      operator: "eq",
      value: "",
      logic: "and",
    },
  ]);

  const [addNewLevel, setAddNewLevel] = useState<
    (ILevel & { id?: string; imageFile?: File }) | null
  >(null);
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showDrawer, setShowDrawer] = useState(false);
  const [levels, setLevels] = useState<ILevel[]>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(handleEditClick, onColumnsSubmit, null, enableDisableLevel)
  );
  const [showArchived, setShowArchived] = useState(false);

  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["name"],
    });

  const filterParams = useMemo(
    () => ({
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort ?? null,
      filter: buildFilters(
        selectedProject?.id,
        dataState.filter,
        showArchived,
        customFilters
      ),
    }),
    [
      page.skip,
      page.take,
      dataState.sort,
      selectedProject?.id,
      dataState.filter,
      showArchived,
    ]
  );

  const { data: levelsData, refetch } = useLevels(filterParams, {
    enabled: !!filterParams,
  });

  const { data: allLevelsData, refetch: refetchAllLevels } = useAllLevels(
    { filter: buildFilters(selectedProject?.id) },
    { enabled: false, forExport: true }
  );

  const excelExport = () => {
    if (_exporter.current) {
      if (levelsData?.data && levelsData.data.length > 0) {
        // Get the formatted data

        // Save with the formatted data
        _exporter.current.save(levelsData?.data);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            _exporter.current?.save(levelsData?.data || []);
          }, 300);
        });
      }
    }
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      enableDisableLevel
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? levels.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleChange = (e: TextBoxChangeEvent | ComboBoxChangeEvent) => {
    let val = e.target.value;
    if (e.target.name === "projectId") {
      val = e.value.id;
    }
    setAddNewLevel((prev: any) => {
      return { ...prev, [e.target.name as any]: val };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const createLevel = () => {
    if (!addNewLevel) return;

    if (!selectedProject || selectedProject.id === "all") {
      ToastService.error("Please select a project");
      return;
    }

    if (!addNewLevel.name) {
      ToastService.error("Please enter level name");
      return;
    }
    if (!addNewLevel.imageFile) {
      ToastService.error("Please upload the level image");
      return;
    }

    levelSvc
      .createLevel(
        addNewLevel.name.trim(),
        selectedProject.id!,
        addNewLevel.imageFile
      )
      .then((res) => {
        if (!res?.error) {
          ToastService.success("Level created successfully");
          queryClient.invalidateQueries({ queryKey: queryKeys.levels.root });
          setShowDrawer(false);

          return;
        }

        ToastService.error(res?.error?.message || "Something went wrong");
      });
  };

  const editLevel = async () => {
    if (!addNewLevel?.id) return;
    const levelData = { ...addNewLevel, action: "draft" };

    updateLevelMutation.mutate(
      { id: levelData.id!, data: levelData },
      {
        onSuccess: async () => {
          if (addNewLevel.imageFile) {
            const res = await levelSvc.uploadImage(
              addNewLevel.id!,
              addNewLevel.imageFile
            );

            if (!res?.data) {
              ToastService.error("Failed to upload image");
              return;
            }

            queryClient.invalidateQueries({
              queryKey: queryKeys.levels.all,
              refetchType: "active", // Include as query keys
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.levels.root,
              refetchType: "active", // Include as query keys
            });

            queryClient.invalidateQueries({
              queryKey: queryKeys.levels.detail,
              refetchType: "active", // Include as query keys
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.levelHistory.root,
              refetchType: "active", // Include as query keys
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.levels.areas,
              refetchType: "active", // Include as query keys
            });
          }

          ToastService.success("Level updated successfully");
          setAddNewLevel(null);
          setShowDrawer(false);
        },
      }
    );
  };

  function enableDisableLevel(dataItem: ILevel) {
    if (!dataItem?.id) return;
    updateLevelMutation.mutate(
      { id: dataItem.id, data: { ...dataItem, isActive: !dataItem.isActive } },
      {
        onSuccess: () => {
          ToastService.success("Level updated successfully");
        },
      }
    );
  }

  function handleEditClick(data: ILevel) {
    setAddNewLevel(data);
    setShowDrawer(true);
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewLevel(null);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (levelsData?.data) {
      setLevels(levelsData.data);
    }
  }, [levelsData]);

  useEffect(() => {
    if (!levels?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableLevel
    );
    setStateColumns(cols);
  }, [levels, columnsMapping]);

  useEffect(() => {
    if (!allLevelsData?.data?.length) return;
    // _exporter.current?.save();
  }, [allLevelsData]);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={levels}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={levelsData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => setShowDrawer(true)}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>
              <div className="flex items-center gap-2 md:order-3 sm:order-2 order-4 sm:mt-0 mt-2 sm:ml-0 ml-auto">
                <Label className="font-medium text-sm font-cabin">
                  Show Inactive
                </Label>
                <Switch
                  defaultChecked={false}
                  onChange={(e) => {
                    setShowArchived(!showArchived);
                  }}
                />
              </div>
              <div
                className="cursor-pointer sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allLevelsData?.data || []}
                  collapsible={true}
                  fileName="Levels.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      {...columnProps(column.field, dataState)}
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    {...columnProps(column.field, dataState)}
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      <WriteDrawer
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        setAddNewLevel={setAddNewLevel}
        addNewLevel={addNewLevel}
        handleChange={handleChange}
        onSave={addNewLevel?.id ? editLevel : createLevel}
      />
    </>
  );
};
